import type { Transaction, YMapEvent } from "yjs";
import * as Y from "yjs";
import type { YMap } from "yjs/dist/src/types/YMap";
import Editor from "~/js/editor";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import type {
	INodePosition,
	IUserTemplateParams
} from "~/solidJs/shared/helpers/types/INodeDefinition";

type IUndoManagerNodeState = INodePosition | IUserTemplateParams;

export class UndoManager {
	doc: Y.Doc;
	manager: Y.UndoManager;
	state: Y.Map<YMap<IUndoManagerNodeState>>;

	constructor() {
		this.doc = new Y.Doc();
		this.state = this.doc.getMap();
		this.manager = new Y.UndoManager(this.state, {
			doc: this.doc,
			ignoreRemoteMapChanges: false
		});
		this.state.observe((event, transaction) => this.nodesObserver(event, transaction));
	}

	undo() {
		EventAnalytics.undo();
		this.manager.undo();
	}

	redo() {
		EventAnalytics.redo();
		this.manager.redo();
	}

	async deleteNodeFromManager(templateParams: IUserTemplateParams[]) {
		for (const params of templateParams) {
			if (!params.uuid) continue;
			let prevNodeState = this.state.get(params.uuid);
			if (!prevNodeState) {
				prevNodeState = this.createNodeState(params.uuid, params);
				this.state.set(params.uuid, prevNodeState);
			} else {
				const prevParams = prevNodeState.get("templateParams")! as IUserTemplateParams;
				const keyes = Object.keys(params);
				for (const key of keyes as (keyof IUserTemplateParams)[]) {
					const value = params[key];
					if (value !== undefined) {
						(prevParams as any)[key] = value;
					}
				}
			}
		}
		for (const node of templateParams) {
			if (!node.uuid) continue;
			this.state.delete(node.uuid);
		}
	}

	createNodeState(uuid: string, userTemplateParams: IUserTemplateParams) {
		const state = new Y.Map<IUndoManagerNodeState>([
			["templateParams", userTemplateParams],
			["position", userTemplateParams.position]
		]);
		state.observe((e, t) => {
			this.nodeStateObserver(e, t, uuid);
		});
		return state;
	}

	nodeStateObserver(
		event: YMapEvent<IUndoManagerNodeState>,
		transaction: Transaction,
		uuid: string
	) {
		if (transaction.origin !== this.manager) return;

		const keysMap = event.keys;

		for (const changetKey of event.keysChanged) {
			const dif = keysMap.get(changetKey);

			if (dif?.action !== "update") continue;
			if (changetKey !== "position") continue;

			const node = Editor.context.graph.nodesHashMap.get(uuid);

			if (!node) continue;

			const newPos = event.target.get("position");

			if (!newPos) continue;
			if (!("x" in newPos)) continue;

			node.layout.setPosition(newPos.x, newPos.y, true);
		}
	}

	nodesObserver(event: YMapEvent<YMap<IUndoManagerNodeState>>, transaction: Transaction) {
		if (transaction.origin !== this.manager) return;

		const keysMap = event.keys;
		const nodesToAdd = [];

		for (const changetKey of event.keysChanged) {
			const dif = keysMap.get(changetKey);

			if (dif?.action === "add") {
				const newNodeStateMap = event.target.get(changetKey);
				newNodeStateMap?.observe((e, t) => this.nodeStateObserver(e, t, changetKey));
				const templateParams = newNodeStateMap?.get("templateParams");
				templateParams && nodesToAdd.push(templateParams);
				continue;
			}
			if (dif?.action === "delete") {
				Editor.context.graph.deleteNodeById(changetKey);
			}
			if (dif?.action === "update") {
				if (this.manager.redoing) {
					setTimeout(() => this.manager.redo(), 0);
				}
				if (this.manager.undoing) {
					setTimeout(() => this.manager.undo(), 0);
				}
			}
		}
		Editor.context.graph.pasteNodes(nodesToAdd, true, true, false);
	}

	addNodeToManager(uuid: string, userTemplateParams: IUserTemplateParams) {
		const state = this.createNodeState(uuid, userTemplateParams);
		this.state.set(uuid, state);
	}

	updateNodePosition(uuid: string, position: INodePosition) {
		const stateNode = this.state.get(uuid);
		if (stateNode) {
			stateNode.set("position", position);
		}
	}
}
